import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import confetti from 'canvas-confetti';

import LazyImage from '../gui/lazyImage';

import { uploadImageFile } from '../../lib/pasteImageUpload';
import { printViaIframe } from '../../lib/printHelper';
import { buildPrizeWinnerSmsUsers } from '../../lib/conventionPrizes';

const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Prize lottery admin drawer (convention.prizes) — officials only.
 *
 * Add prizes (title + image), draw winners one at a time (a live ceremony
 * moment with confetti) or all at once, undo a draw, re-draw a prize the
 * winner never picked up (the no-show is forfeited and excluded from later
 * draws, until reinstated), and mark prizes as picked up in the kiosk. The
 * winner is ALWAYS picked server-side with a CSPRNG among all convention
 * participants — one ticket per person, one prize max per person (see
 * backend/lib/convention-prizes.js).
 */
@observer
class ConventionPrizesAdmin extends Component {
    constructor(props) {
        super(props);
        this.state = {
            newTitle: '',
            newImage: null,
            savingTitles: {},
        };
        this.fileInput = null;
        this.updateTimers = {};
    }

    componentWillUnmount() {
        Object.keys(this.updateTimers).forEach(key => clearTimeout(this.updateTimers[key]));
    }

    setPrizes = prizes => {
        const { conventionStore } = this.props.stores;
        conventionStore.updateObjectKeyValue('convention', 'prizes', prizes);
    }

    getPrizes = () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        return Array.isArray(convention.prizes) ? convention.prizes : [];
    }

    // Printable A4 wall list of prizes and current winners — same iframe print
    // path as the QR poster and signup notes in stagesKanban. Forfeited no-shows
    // are left out: the sheet shows who holds each prize right now.
    printPrizeList = () => {
        const { conventionStore } = this.props.stores;
        const { convention, participants: userRecords = [] } = conventionStore;
        const prizes = this.getPrizes();
        if (!convention || prizes.length === 0) return;

        const dateStr = convention.startDate
            ? util.formatDate(convention.startDate, {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', locale: 'nb-NO',
            })
            : '';
        const printedAt = util.formatDate(new Date(), {
            hour12: false, hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'long', locale: 'nb-NO',
        });
        const rows = prizes.map((prize, idx) => {
            const winner = prize.winner && prize.winner.id > 0 ? prize.winner : null;
            const winnerName = winner ? `${winner.firstname || ''} ${winner.lastname || ''}`.trim() : '';
            const winnerClub = winner
                ? winner.sportsClub || (userRecords.find(u => u && u.id === winner.id) || {}).sportsClub || ''
                : '';
            const image = prize.image || {};
            const imgSrc = image.s3MediumLink || image.s3SmallLink || image.s3Link || '';
            const claimed = !!prize.claimed;
            return `
                <div class="prize-row${claimed ? ' claimed' : ''}">
                    <div class="prize-no">${idx + 1}</div>
                    <div class="prize-img">${imgSrc
        ? `<img src="${escapeHtml(imgSrc)}" alt="" />`
        : '<div class="prize-img-empty"></div>'}</div>
                    <div class="prize-text">
                        <div class="prize-title">${escapeHtml(prize.title || '')}</div>
                        ${winner
        ? `<div class="prize-winner">${escapeHtml(winnerName)}${winnerClub ? `<span class="prize-club">${escapeHtml(winnerClub)}</span>` : ''}</div>`
        : '<div class="prize-undrawn">Ikke trukket enn&aring;</div>'}
                    </div>
                    <div class="prize-status">${claimed ? '&#10003; Hentet' : ''}</div>
                </div>`;
        }).join('');

        const html = `
            <div class="prize-sheet">
                <div class="prize-head">
                    <h1>${escapeHtml(convention.title || '')}</h1>
                    ${dateStr ? `<div class="prize-date">${escapeHtml(dateStr)}</div>` : ''}
                    <h2>Premier</h2>
                    <div class="prize-count">${prizes.length} premier</div>
                </div>
                ${rows}
                <div class="prize-foot">Premier hentes i kiosken. Utskrift ${escapeHtml(printedAt)}.</div>
            </div>
        `;
        const customCss = `
            .prize-sheet { font-family: Arial, Helvetica, sans-serif; padding: 6mm 4mm; color: #000; }
            .prize-head { text-align: center; margin-bottom: 6mm; }
            .prize-head h1 { font-size: 24pt; margin: 0 0 2mm 0; }
            .prize-head .prize-date { font-size: 12pt; color: #444; margin-bottom: 4mm; }
            .prize-head h2 { font-size: 30pt; margin: 0; letter-spacing: 1px; }
            .prize-head .prize-count { font-size: 11pt; color: #444; }
            .prize-row { display: flex; align-items: center; gap: 5mm; padding: 3mm 2mm; border-bottom: 1px solid #ccc; page-break-inside: avoid; break-inside: avoid; }
            .prize-no { flex: 0 0 10mm; font-size: 16pt; font-weight: bold; color: #666; text-align: right; }
            .prize-img { flex: 0 0 22mm; }
            .prize-img img { width: 22mm; height: 22mm; object-fit: cover; border-radius: 2mm; display: block; }
            .prize-img-empty { width: 22mm; height: 22mm; border-radius: 2mm; background: #eee; }
            .prize-text { flex: 1 1 auto; min-width: 0; }
            .prize-title { font-size: 15pt; margin-bottom: 1.5mm; }
            .prize-winner { font-size: 18pt; font-weight: bold; }
            .prize-club { font-size: 11pt; font-weight: normal; color: #444; margin-left: 3mm; }
            .prize-undrawn { font-size: 13pt; color: #888; font-style: italic; }
            .prize-status { flex: 0 0 28mm; text-align: right; font-size: 12pt; color: #2a7; font-weight: bold; }
            .prize-row.claimed .prize-title, .prize-row.claimed .prize-winner { color: #555; }
            .prize-foot { margin-top: 8mm; text-align: center; font-size: 11pt; color: #444; }
        `;
        printViaIframe(html, { customCss, waitForImages: true });
    }

    // "SMS til vinnere": open the generic sendSms drawer (same path as the
    // training SMS) with one personalized message per winner holding a prize
    // not yet picked up. Nothing is sent until the desk presses send there.
    sendSmsToWinners = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { convention, participants: userRecords = [] } = conventionStore;
        const { drawerHeightMediumLarge } = appState;
        const { users } = buildPrizeWinnerSmsUsers(
            this.getPrizes(), userRecords, convention.participants, convention.title,
        );
        if (users.length === 0) return;

        appState.openDrawer('sendSms', {
            height: drawerHeightMediumLarge,
            users,
            personalized: true,
            message: `Gratulerer {{fornavn}}! Du har vunnet {{premie}} i premietrekningen på {{stevne}}. Premien hentes i kiosken. Hilsen ${convention.organizer || 'arrangøren'}`,
            from: convention.title || 'Stevne',
            // Provider-valid SMS sender (a-zA-Z0-9, max 11 chars); the title is
            // only a seed and is sanitized by the sendSms drawer.
            editableSender: true,
        }, drawerLevel + 1);
    }

    pickImage = () => {
        if (this.fileInput) {
            this.fileInput.click();
        }
    }

    onImageChange = async e => {
        const file = (e.target.files || [])[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        this.setState({ uploadingImage: true, error: null });
        try {
            const uploaded = await uploadImageFile(file);
            this.setState({ newImage: uploaded, uploadingImage: false });
        } catch (err) {
            this.setState({ uploadingImage: false, error: `Upload failed: ${err.message}` });
        }
    }

    addPrize = async () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { newTitle, newImage } = this.state;
        if (!newTitle.trim()) {
            return;
        }
        this.setState({ addingPrize: true, error: null });
        const response = await util.fetchApi(`/api/conventions/${convention.id}/prizes`, { method: 'POST' }, {
            title: newTitle,
            image: newImage || undefined,
        });
        if (response && response.status === 201 && response.data && response.data.prize) {
            this.setPrizes([...this.getPrizes(), response.data.prize]);
            this.setState({ newTitle: '', newImage: null, addingPrize: false });
        } else {
            this.setState({ addingPrize: false, error: response?.message || 'Could not add prize' });
        }
    }

    // Inline title edit — array-object style auto-save with the standard
    // 750ms debounce (no Save button).
    onTitleInput = (prize, value) => {
        const updated = this.getPrizes().map(p => (p && p.md5 === prize.md5 ? { ...p, title: value } : p));
        this.setPrizes(updated);
        this.setState(prevState => ({ savingTitles: { ...prevState.savingTitles, [prize.md5]: true } }));
        if (this.updateTimers[prize.md5]) {
            clearTimeout(this.updateTimers[prize.md5]);
        }
        this.updateTimers[prize.md5] = setTimeout(() => this.saveTitle(prize.md5, value), 750);
    }

    saveTitle = async (md5, title) => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (title.trim()) {
            await util.fetchApi(`/api/conventions/${convention.id}/prizes/${md5}`, { method: 'PATCH' }, {
                title,
            });
        }
        this.setState(prevState => ({ savingTitles: { ...prevState.savingTitles, [md5]: false } }));
    }

    deletePrize = async prize => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const response = await util.fetchApi(`/api/conventions/${convention.id}/prizes/${prize.md5}`, { method: 'DELETE' }, {});
        if (response && (response.status === 202 || response.status === 200)) {
            this.setPrizes(this.getPrizes().filter(p => !p || p.md5 !== prize.md5));
        }
    }

    fireConfetti = () => {
        // The drawer sits high in the stacking order — lift the confetti
        // canvas above it so the celebration is actually visible.
        confetti({
            particleCount: 150,
            spread: 80,
            origin: { y: 0.7 },
            zIndex: 100000,
        });
    }

    drawPrize = async prize => {
        await this.runDraw(prize.md5, { md5: prize.md5 });
    }

    // Re-draw a prize the winner never picked up. The server forfeits the
    // current winner (excluded from every later draw) and picks a new one.
    redrawPrize = async prize => {
        await this.runDraw(prize.md5, { md5: prize.md5, redraw: true });
    }

    runDraw = async (drawingKey, body) => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        this.setState({ drawing: drawingKey, error: null });
        const start = Date.now();
        const response = await util.fetchApi(`/api/conventions/${convention.id}/prizes/draw`, { method: 'POST' }, body);
        // A little suspense before the reveal — the draw itself is instant.
        const elapsed = Date.now() - start;
        if (elapsed < 1200) {
            await new Promise(resolve => setTimeout(resolve, 1200 - elapsed));
        }
        if (response && response.status === 200 && response.data && response.data.prizes) {
            this.setPrizes(response.data.prizes);
            this.fireConfetti();
        } else {
            this.setState({ error: response?.message || 'Draw failed' });
        }
        this.setState({ drawing: null });
    }

    drawAll = async () => {
        await this.runDraw('all', { all: true });
    }

    toggleClaimed = async prize => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const claimed = !prize.claimed;
        const response = await util.fetchApi(`/api/conventions/${convention.id}/prizes/${prize.md5}`, { method: 'PATCH' }, {
            claimed,
        });
        if (response && (response.status === 202 || response.status === 200) && response.data && response.data.prize) {
            this.setPrizes(this.getPrizes().map(p => (p && p.md5 === prize.md5 ? response.data.prize : p)));
        }
    }

    resetWinner = async prize => {
        await this.patchPrize(prize, { resetWinner: true });
    }

    // Put a forfeited no-show back in the ticket pool.
    reinstateWinner = async (prize, forfeited) => {
        await this.patchPrize(prize, { reinstateWinner: forfeited.id });
    }

    patchPrize = async (prize, body) => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const response = await util.fetchApi(`/api/conventions/${convention.id}/prizes/${prize.md5}`, { method: 'PATCH' }, body);
        if (response && (response.status === 202 || response.status === 200) && response.data && response.data.prize) {
            this.setPrizes(this.getPrizes().map(p => (p && p.md5 === prize.md5 ? response.data.prize : p)));
        }
    }

    render() {
        const { conventionStore } = this.props.stores;
        // conventionStore.participants holds the full user records get-convention
        // loads alongside the convention — the sportsClub fallback for winners
        // drawn before the club was stored denormalized on the prize.
        const { convention, participants: userRecords = [] } = conventionStore;
        const {
            newTitle, newImage, uploadingImage, addingPrize, drawing, error, savingTitles,
        } = this.state;

        if (!convention || !convention.id) {
            return null;
        }
        const prizes = this.getPrizes();
        const undrawn = prizes.filter(p => p && !(p.winner && p.winner.id > 0));
        const isSaving = Object.keys(savingTitles).some(key => savingTitles[key]);

        // Mirror of the server-side ticket pool (one ticket per person, winners
        // and forfeited no-shows excluded) — display only; the server is authoritative.
        const wonIds = new Set();
        prizes.forEach(p => {
            if (p && p.winner && p.winner.id > 0) {
                wonIds.add(p.winner.id);
            }
            (p && Array.isArray(p.forfeitedWinners) ? p.forfeitedWinners : []).forEach(f => {
                if (f && f.id > 0) {
                    wonIds.add(f.id);
                }
            });
        });
        const poolCount = new Set((convention.participants || [])
            .map(p => p && p.id)
            .filter(id => id > 0 && !wonIds.has(id))).size;
        const winnerSms = buildPrizeWinnerSmsUsers(prizes, userRecords, convention.participants, convention.title);

        return (
            <div class='w-100 d-flex justify-content-center position-relative'>
                <div class='w-100 px-3' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter box-header mt-0 d-flex flex-row justify-content-between align-items-center'>
                        <span>
                            <i class='fa-duotone fa-gift me-2' />
                            <Text id='convention.prizes-admin-title'>Prize lottery</Text>
                        </span>
                        <span class='d-flex flex-row align-items-center gap-2'>
                            {isSaving ? (
                                <small class='text-body-secondary'>
                                    <i class='fa-duotone fa-spinner fa-spin me-1' />
                                    <Text id='convention.prizes-saving'>Saving…</Text>
                                </small>
                            ) : (
                                <small class='text-body-secondary'>
                                    <Text id='convention.prizes-autosave'>Changes are saved automatically</Text>
                                </small>
                            )}
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                onClick={this.printPrizeList}
                                disabled={prizes.length === 0}
                                title='Skriv ut premieliste for veggen'
                            >
                                <i class='fa-solid fa-print me-1' />
                                <Text id='convention.prizes-print'>Print</Text>
                            </button>
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-primary rounded-pill'
                                onClick={this.sendSmsToWinners}
                                disabled={winnerSms.users.length === 0}
                                title='SMS til vinnere som ikke har hentet premien'
                            >
                                <i class='fa-duotone fa-message-sms me-1' />
                                <Text id='convention.prizes-sms-winners' fields={{ count: winnerSms.users.length }}>
                                    SMS to winners ({winnerSms.users.length})
                                </Text>
                            </button>
                        </span>
                    </div>
                    {winnerSms.noPhone.length > 0 && (
                        <div class='text-body-secondary mb-2' style='font-size: 0.85rem;'>
                            <i class='fa-duotone fa-phone-slash me-1' />
                            <Text id='convention.prizes-sms-no-phone'>Winners without a mobile number:</Text>
                            {' '}
                            {winnerSms.noPhone.map(w => `${w.firstname} ${w.lastname}`.trim()).join(', ')}
                        </div>
                    )}

                    <div class='text-body-secondary mb-3' style='font-size: 0.9rem;'>
                        <i class='fa-duotone fa-ticket me-1' />
                        <Text id='convention.prizes-admin-pool' fields={{ count: poolCount }}>
                            {poolCount} participants in the pool
                        </Text>
                        <span class='ms-2'>
                            <i class='fa-duotone fa-shield-check me-1' />
                            <Text id='convention.prizes-admin-random'>Winners are drawn with real randomness (CSPRNG) — one prize max per person.</Text>
                        </span>
                        <span class='ms-2'>
                            <i class='fa-duotone fa-user-slash me-1' />
                            <Text id='convention.prizes-admin-noshow'>A winner who does not pick up the prize can be re-drawn and is then excluded from further draws.</Text>
                        </span>
                    </div>

                    {error && (
                        <div class='alert alert-danger py-2' style='font-size: 0.85rem;'>
                            {error}
                        </div>
                    )}

                    {/* Add prize */}
                    <div class='card mb-3'>
                        <div class='card-body p-3'>
                            <div class='fw-bold mb-2' style='font-size: 0.9rem;'>
                                <i class='fa-solid fa-plus me-1' />
                                <Text id='convention.prizes-add'>Add prize</Text>
                            </div>
                            <div class='d-flex flex-row flex-wrap gap-2 align-items-center'>
                                <Localizer>
                                    <input
                                        type='text'
                                        class='form-control'
                                        style='max-width: 350px;'
                                        placeholder={<Text id='convention.prizes-add-title-placeholder'>Prize title (e.g. Gift card 500,-)</Text>}
                                        value={newTitle}
                                        onInput={e => this.setState({ newTitle: e.target.value })}
                                    />
                                </Localizer>
                                <input
                                    type='file'
                                    accept='image/*'
                                    style='display: none;'
                                    ref={c => this.fileInput = c}
                                    onChange={this.onImageChange}
                                />
                                {newImage ? (
                                    <div class='d-flex align-items-center gap-1'>
                                        <img
                                            src={newImage.s3SmallLink || newImage.s3Link}
                                            alt=''
                                            style='height: 38px; width: 38px; object-fit: cover; border-radius: 5px;'
                                        />
                                        <i
                                            class='fa-solid fa-xmark text-danger'
                                            style='cursor: pointer;'
                                            onClick={() => this.setState({ newImage: null })}
                                        />
                                    </div>
                                ) : (
                                    <button
                                        type='button'
                                        class='btn btn-outline-secondary rounded-pill'
                                        onClick={this.pickImage}
                                        disabled={uploadingImage}
                                    >
                                        {uploadingImage ? <>
                                            <i class='fa-duotone fa-spinner fa-spin me-1' />
                                        </> : <>
                                            <i class='fa-duotone fa-image me-1' />
                                        </>}
                                        <Text id='convention.prizes-add-image'>Choose image</Text>
                                    </button>
                                )}
                                <button
                                    type='button'
                                    class='btn btn-primary rounded-pill'
                                    onClick={this.addPrize}
                                    disabled={!newTitle.trim() || addingPrize || uploadingImage}
                                >
                                    {addingPrize ? <>
                                        <i class='fa-duotone fa-spinner fa-spin me-1' />
                                    </> : <>
                                        <i class='fa-duotone fa-gift me-1' />
                                    </>}
                                    <Text id='convention.prizes-add'>Add prize</Text>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Draw all */}
                    {undrawn.length > 1 && (
                        <button
                            type='button'
                            class='btn btn-success rounded-pill w-100 mb-3'
                            onClick={this.drawAll}
                            disabled={!!drawing || poolCount === 0}
                        >
                            {drawing === 'all' ? <>
                                <i class='fa-duotone fa-dice fa-spin me-2' />
                                <Text id='convention.prizes-drawing'>Drawing…</Text>
                            </> : <>
                                <i class='fa-duotone fa-dice me-2' />
                                <Text id='convention.prizes-draw-all'>Draw all remaining</Text>
                                <span class='ms-1'>({undrawn.length})</span>
                            </>}
                        </button>
                    )}

                    {prizes.length === 0 && (
                        <div class='text-center text-body-secondary py-4'>
                            <i class='fa-duotone fa-gift mb-2' style='font-size: 2.5rem;' /><br />
                            <Text id='convention.prizes-empty'>No prizes yet — add the first one!</Text>
                        </div>
                    )}

                    {/* Prize list */}
                    {prizes.map(prize => {
                        const winner = prize.winner && prize.winner.id > 0 ? prize.winner : null;
                        const winnerName = winner ? `${winner.firstname || ''} ${winner.lastname || ''}`.trim() : '';
                        const winnerClub = winner
                            ? winner.sportsClub || (userRecords.find(u => u && u.id === winner.id) || {}).sportsClub
                            : null;
                        const image = prize.image || {};
                        const isDrawingThis = drawing === prize.md5;
                        const forfeited = Array.isArray(prize.forfeitedWinners) ? prize.forfeitedWinners : [];
                        return (
                            <div key={prize.md5} class='card mb-2'>
                                <div class='card-body p-2 d-flex flex-row align-items-center gap-2'>
                                    {image.s3SmallLink || image.s3MediumLink || image.s3Link ? (
                                        <LazyImage
                                            className='rounded overflow-hidden flex-shrink-0'
                                            src={image.s3SmallLink || image.s3MediumLink || image.s3Link}
                                            lowResBase64={image.lowResBase64}
                                            alt={prize.title || ''}
                                            loading='lazy'
                                            style='height: 60px; width: 60px; object-fit: cover;'
                                        />
                                    ) : (
                                        <div class='rounded d-flex align-items-center justify-content-center bg-body-secondary flex-shrink-0' style='height: 60px; width: 60px;'>
                                            <i class='fa-duotone fa-gift text-body-secondary' style='font-size: 1.5rem;' />
                                        </div>
                                    )}
                                    <div class='flex-grow-1' style='min-width: 0;'>
                                        <input
                                            type='text'
                                            class='form-control form-control-sm mb-1'
                                            value={prize.title}
                                            onInput={e => this.onTitleInput(prize, e.target.value)}
                                        />
                                        {winner ? <>
                                            <div style='font-size: 0.85rem;'>
                                                <i class='fa-duotone fa-trophy text-warning me-1' />
                                                <strong>{winnerName}</strong>
                                                {winnerClub && (
                                                    <span class='text-body-secondary ms-1' style='font-size: 0.75rem;'>{winnerClub}</span>
                                                )}
                                            </div>
                                            <label class='d-inline-flex align-items-center mb-0 mt-1' style='font-size: 0.8rem; cursor: pointer;'>
                                                <input
                                                    type='checkbox'
                                                    class='form-check-input me-1 mt-0'
                                                    checked={!!prize.claimed}
                                                    onChange={() => this.toggleClaimed(prize)}
                                                />
                                                <Text id='convention.prizes-claimed-toggle'>Picked up in kiosk</Text>
                                            </label>
                                            {forfeited.length > 0 && (
                                                <div class='text-body-secondary mt-1' style='font-size: 0.75rem;'>
                                                    <i class='fa-duotone fa-user-slash me-1' />
                                                    <Text id='convention.prizes-forfeited'>Did not pick up:</Text>
                                                    {forfeited.map(f => (
                                                        <span key={f.id} class='ms-1 text-nowrap'>
                                                            <s>{`${f.firstname || ''} ${f.lastname || ''}`.trim()}</s>
                                                            <Localizer>
                                                                <i
                                                                    class='fa-solid fa-rotate-left ms-1'
                                                                    style='cursor: pointer;'
                                                                    title={<Text id='convention.prizes-reinstate'>Reinstate in the pool</Text>}
                                                                    onClick={() => this.reinstateWinner(prize, f)}
                                                                />
                                                            </Localizer>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </> : (
                                            <div class='text-body-secondary' style='font-size: 0.8rem;'>
                                                <Text id='convention.prize-not-drawn'>Not drawn yet</Text>
                                            </div>
                                        )}
                                    </div>
                                    <div class='d-flex flex-column align-items-end gap-1 flex-shrink-0'>
                                        {winner ? <>
                                            {!prize.claimed && (
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-outline-warning rounded-pill'
                                                    onClick={() => this.redrawPrize(prize)}
                                                    disabled={!!drawing || poolCount === 0}
                                                >
                                                    {isDrawingThis ? <>
                                                        <i class='fa-duotone fa-dice fa-spin me-1' />
                                                        <Text id='convention.prizes-drawing'>Drawing…</Text>
                                                    </> : <>
                                                        <i class='fa-duotone fa-dice me-1' />
                                                        <Text id='convention.prizes-redraw'>Re-draw</Text>
                                                    </>}
                                                </button>
                                            )}
                                            <Localizer>
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-outline-secondary rounded-pill'
                                                    onClick={() => this.resetWinner(prize)}
                                                    title={<Text id='convention.prizes-reset'>Reset draw</Text>}
                                                >
                                                    <i class='fa-solid fa-rotate-left' />
                                                </button>
                                            </Localizer>
                                        </> : <>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-success rounded-pill'
                                                onClick={() => this.drawPrize(prize)}
                                                disabled={!!drawing || poolCount === 0}
                                            >
                                                {isDrawingThis ? <>
                                                    <i class='fa-duotone fa-dice fa-spin me-1' />
                                                    <Text id='convention.prizes-drawing'>Drawing…</Text>
                                                </> : <>
                                                    <i class='fa-duotone fa-dice me-1' />
                                                    <Text id='convention.prizes-draw'>Draw winner</Text>
                                                </>}
                                            </button>
                                            <i
                                                class='fa-duotone fa-trash text-danger'
                                                style='cursor: pointer; font-size: 0.85rem;'
                                                onClick={() => this.deletePrize(prize)}
                                            />
                                        </>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

export default ConventionPrizesAdmin;
